import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Heart, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { PageHero, SectionLabel } from "@/components/Section";
import { DONATION, IMAGES, EVENT, CONTACT, PAYMENTS } from "@/data/content";
import { api } from "@/lib/api";
import { PaymentMethod } from "@/components/PaymentMethod";
import { FoundationLink } from "@/components/FoundationLink";

const inputCls =
  "w-full border-b-2 border-border bg-transparent px-1 py-2.5 text-espresso rounded-none focus:outline-none focus:border-rust transition-colors placeholder:text-muted-foreground/60";

export default function Donate() {
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState(250);
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("one-time");
  const [coverFees, setCoverFees] = useState(false);
  const [method, setMethod] = useState(PAYMENTS.donation ? "card" : "check");
  const payByCard = PAYMENTS.donation && method === "card";
  const [form, setForm] = useState({ name: "", email: "", phone: "", in_honor_of: "", message: "" });

  const effectiveAmount = custom ? Number(custom) : amount;
  const chargedAmount = payByCard && coverFees && effectiveAmount > 0
    ? Math.round(((effectiveAmount + 0.30) / (1 - 0.029)) * 100) / 100
    : effectiveAmount;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    if (!effectiveAmount || effectiveAmount <= 0) {
      toast.error("Please choose or enter a donation amount.");
      return;
    }
    setLoading(true);

    if (payByCard) {
      try {
        const { data } = await api.post("/checkout/donation", {
          name: form.name,
          email: form.email,
          phone: form.phone,
          amount: effectiveAmount,
          frequency,
          in_honor_of: form.in_honor_of,
          message: form.message,
          cover_fees: coverFees,
          origin_url: window.location.origin,
        });
        window.location.href = data.url;
      } catch (err) {
        setLoading(false);
        toast.error("Unable to start checkout. Please try again.");
      }
      return;
    }

    try {
      const { data } = await api.post("/submit/donation", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        amount: effectiveAmount,
        frequency,
        in_honor_of: form.in_honor_of,
        message: form.message,
      });
      toast.success(data.message || "Thank you! Your donation pledge was received.");
      setForm({ name: "", email: "", phone: "", in_honor_of: "", message: "" });
      setCustom("");
      setAmount(250);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="donate-page">
      <PageHero
        overline="Give Back"
        title="Make A"
        accent="Donation"
        subtitle={DONATION.blurb}
        image={IMAGES.firefighterSmoke}
      />

      <section className="paper-noise bg-background">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-12 relative z-10">
          <div className="lg:col-span-5">
            <SectionLabel>Every Dollar Counts</SectionLabel>
            <h2 className="mt-5 font-serif font-black text-4xl text-espresso leading-tight">Support Those Who Serve</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Your gift goes directly toward the mission of the {EVENT.beneficiary} — providing financial assistance,
              cancer and behavioral-health support, and scholarships for the first responders who serve our community.
            </p>
            <div className="mt-8 flex items-start gap-3 border border-border bg-card p-5 hard-shadow-sm">
              <ShieldCheck className="text-rust shrink-0 mt-0.5" size={20} />
              <p className="text-sm text-muted-foreground">
                Donations are collected by the{" "}
                <FoundationLink testId="donate-foundation-site-link" className="text-espresso font-medium underline decoration-rust/60 underline-offset-2 hover:text-rust transition-colors" icon={false}>Wayne Walker Foundation</FoundationLink>{" "}
                in support of our first responders. Pay securely online by card, or pledge now and pay by check.
              </p>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Questions? Contact <span className="text-rust font-medium">{CONTACT.sponsorEmail}</span> · {CONTACT.sponsorPhone}
            </p>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="border border-border bg-card p-8 hard-shadow-sm" data-testid="donate-form">
              <h3 className="font-serif font-bold text-2xl text-espresso flex items-center gap-2"><Heart size={20} className="text-rust" /> Your Gift</h3>

              {/* Amount presets */}
              <div className="mt-6">
                <label className="label-font text-[11px] text-espresso/70">Amount *</label>
                <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {DONATION.presets.map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`donate-preset-${p}`}
                      onClick={() => { setAmount(p); setCustom(""); }}
                      className={`label-font text-sm py-3 border transition-colors ${!custom && amount === p ? "bg-rust text-primary-foreground border-espresso" : "border-border text-espresso hover:border-rust"}`}
                    >
                      ${p}
                    </button>
                  ))}
                </div>
                <input
                  data-testid="donate-custom-amount"
                  type="number"
                  min="1"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="Or enter a custom amount"
                  className={`${inputCls} mt-4`}
                />
              </div>

              {/* Frequency */}
              <div className="mt-6">
                <label className="label-font text-[11px] text-espresso/70">Frequency</label>
                <div className="mt-3 flex gap-2">
                  {["one-time", "monthly"].map((f) => (
                    <button
                      key={f}
                      type="button"
                      data-testid={`donate-frequency-${f}`}
                      onClick={() => setFrequency(f)}
                      className={`label-font text-xs px-5 py-2.5 border capitalize transition-colors ${frequency === f ? "bg-espresso text-cream border-espresso" : "border-border text-espresso hover:border-rust"}`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-font text-[11px] text-espresso/70">Full Name *</label>
                  <input data-testid="donate-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
                </div>
                <div>
                  <label className="label-font text-[11px] text-espresso/70">Email *</label>
                  <input data-testid="donate-email" type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
                </div>
              </div>
              <div className="mt-6 grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-font text-[11px] text-espresso/70">Phone</label>
                  <input data-testid="donate-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(713) 555-0100" />
                </div>
                <div>
                  <label className="label-font text-[11px] text-espresso/70">In Honor Of (optional)</label>
                  <input data-testid="donate-honor" className={inputCls} value={form.in_honor_of} onChange={(e) => setForm({ ...form, in_honor_of: e.target.value })} placeholder="A first responder, loved one…" />
                </div>
              </div>
              <div className="mt-6">
                <label className="label-font text-[11px] text-espresso/70">Message (optional)</label>
                <textarea data-testid="donate-message" rows={3} className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Share why you're giving…" />
              </div>

              {PAYMENTS.donation && (
                <div className="mt-6">
                  <PaymentMethod value={method} onChange={setMethod} prefix="donate" compact />
                </div>
              )}

              {/* Cover fees */}
              {payByCard && (
              <label data-testid="donate-cover-fees" className="mt-6 flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={coverFees}
                  onChange={(e) => setCoverFees(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-rust shrink-0"
                />
                <span className="text-sm text-espresso">
                  Cover the processing fee so 100% of my gift reaches the Foundation
                  {coverFees && effectiveAmount > 0 && (
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      You'll be charged ${chargedAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })} · ${Number(effectiveAmount).toLocaleString()} reaches the Foundation
                    </span>
                  )}
                </span>
              </label>
              )}

              <button
                data-testid="donate-submit-btn"
                type="submit"
                disabled={loading}
                className="mt-8 w-full label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-espresso flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60 hard-shadow-sm"
              >
                {loading
                  ? <><Loader2 size={16} className="animate-spin" /> {payByCard ? "Redirecting…" : "Submitting…"}</>
                  : <>{payByCard ? "Donate" : "Pledge"} ${chargedAmount ? Number(chargedAmount).toLocaleString() : "0"} <Heart size={15} /></>}
              </button>
              <p className="mt-3 text-xs text-muted-foreground text-center">
                {payByCard
                  ? <>Secure checkout powered by Stripe. Your gift supports the Wayne Walker Foundation.</>
                  : PAYMENTS.donation
                    ? <>Submit your pledge and we'll email check / mailing instructions to complete your gift.</>
                    : <>Online payments coming soon — submit your pledge and we'll follow up to complete your gift.</>}
              </p>
            </form>
          </div>
        </div>
      </section>

      <section className="bg-espresso text-cream">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 text-center">
          <h2 className="font-serif font-black text-3xl sm:text-4xl">Prefer To Play Or Sponsor?</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/register" className="label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-cream/20 transition-transform hover:-translate-y-1 hard-shadow flex items-center gap-2">Register a Team <ArrowRight size={16} /></Link>
            <Link to="/sponsor" className="label-font text-sm border border-cream/40 px-8 py-4 transition-colors hover:bg-cream/10">Become a Sponsor</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
